import {
  createContext,
  useContext,
  useCallback,
  useRef,
  type ReactNode,
} from 'react';
import type { SavedPair } from '../types/accessibility';

// ── Storage key ──────────────────────────────────────────────────────────
const STORAGE_KEY = 'chromis-tool-state';

// ── Per-tool state type map ──────────────────────────────────────────────
// Each tool declares the shape of the state it wants to persist.
// The context stores arbitrary JSON-serialisable objects keyed by tool route.

export interface ToolStateMap {
  compare: {
    color2Input: string;
    selectedIlluminant: 'D50' | 'D65';
    showAnimation: boolean;
  };
  semantic: {
    query: string;
  };
  nearest: {
    deltaMethod: string;
    deltaThreshold: number;
    datasetFilter: string;
    searchQuery: string;
  };
  accessibility: {
    foreground: string;
    background: string;
    foregroundAlpha: number;
    backgroundAlpha: number;
    textSize: number;
    isBold: boolean;
    targetCompliance: string;
    savedPairs: SavedPair[];
  };
  print: {
    selectedProfileId: string;
  };
  settings: {
    activeTab: string;
    performanceSubTab?: string;
  };
  tokens: {
    tokenSets: Array<{
      id: string;
      name: string;
      createdAt: number;
      modifiedAt: number;
      $schema?: string;
      $metadata?: Record<string, unknown>;
      $extensions?: Record<string, unknown>;
      tokens?: Record<string, unknown>;
    }>;
    selectedTokenSetId: string | null;
    viewMode: 'grid' | 'list';
    searchQuery: string;
  };
  education: {
    activeTab: string;
  };
}

export type ToolId = keyof ToolStateMap;

// ── Context shape ────────────────────────────────────────────────────────
export interface ToolStateContextType {
  getToolState: <T extends ToolId>(toolId: T) => ToolStateMap[T] | undefined;
  setToolState: <T extends ToolId>(toolId: T, state: ToolStateMap[T]) => void;
}

const ToolStateContext = createContext<ToolStateContextType | undefined>(undefined);

// ── Helpers ──────────────────────────────────────────────────────────────
const MAX_TOOLS = 3; // LRU eviction limit

function loadFromStorage(): Record<string, unknown> {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveToStorage(state: Record<string, unknown>): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Quota exceeded — apply LRU eviction
    const keys = Object.keys(state);
    while (keys.length > MAX_TOOLS) {
      const oldest = keys.shift();
      if (oldest) delete state[oldest];
    }
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      // Give up silently — non-critical feature
    }
  }
}

// ── Provider ─────────────────────────────────────────────────────────────
export function ToolStateProvider({ children }: { children: ReactNode }) {
  // We keep a mutable ref to avoid re-rendering all consumers when any
  // tool updates its state. Each tool reads on mount and writes on change.
  const stateRef = useRef<Record<string, unknown>>(loadFromStorage());

  const getToolState = useCallback(
    <T extends ToolId,>(toolId: T): ToolStateMap[T] | undefined => {
      return stateRef.current[toolId] as ToolStateMap[T] | undefined;
    },
    [],
  );

  const setToolState = useCallback(
    <T extends ToolId,>(toolId: T, state: ToolStateMap[T]): void => {
      stateRef.current[toolId] = state;
      saveToStorage(stateRef.current);
    },
    [],
  );

  return (
    <ToolStateContext.Provider value={{ getToolState, setToolState }}>
      {children}
    </ToolStateContext.Provider>
  );
}

// ── Consumer hook ────────────────────────────────────────────────────────
// eslint-disable-next-line react-refresh/only-export-components
export function useToolState(): ToolStateContextType {
  const ctx = useContext(ToolStateContext);
  if (!ctx) throw new Error('useToolState must be used within a ToolStateProvider');
  return ctx;
}
